import React, { FC, ReactNode, useEffect } from 'react'
import {
  Button,
  ModalActions,
  ModalContent,
  ModalHeader,
} from '@lightspeed/design-system-react'
import { VendImage } from '@vend/react-utilities'
import { ApiRetailerPaymentType, ApiTransaction } from '@vend/types'
import VendNumber, { vn } from 'vend-number'
import { LSPAYModalContent } from '../modal-states/LSPAYModalContent'
import { ErrorContent } from '../modal-states/ErrorContent'
import { useRefundEffectReducer } from '../../hooks/use-refund-effect-reducer/useRefundEffectReducer'
import {
  RefundState,
  UnrefRefundReason,
} from '../../hooks/use-refund-effect-reducer/types'
import { ConnectingToTerminal } from '../modal-states/ConnectingToTerminal'
import { CheckingPairing } from '../modal-states/CheckingPairing'
import { Processing } from '../modal-states/Processing'
import { Register } from '../../helpers/lspay.types'

const CARD_PRESENT_NEEDED_HEADER =
  'The refund needs a card present to continue.'
const CHOOSE_ANOTHER_REFUND_TYPE = 'Choose Another Refund Type'

export interface LSPAYRefundClientDrivenProps {
  register: Register
  retailerPaymentType: ApiRetailerPaymentType
  originalRegisterSalePaymentIds?: string[]
  originalSalePaidByOtherPT?: boolean
  originalSaleHasProcessingServicePayment?: boolean
  // Total amount from the original LSPay payment(s) for split-tender detection
  originalLspayPaymentAmount?: number
  outletId: string
  amount: VendNumber
  transactionId: string
  currency: string
  setDismissible: (dismissible: boolean) => void
  onSuccess: (transaction: Promise<ApiTransaction>) => void
  onError: () => void
}

const isDismissible = (refundState: RefundState): boolean =>
  !!refundState.dismissible

const getUnreferencedMessage = (
  reason: UnrefRefundReason,
  retailerPaymentTypeName: string
): string => {
  switch (reason) {
    case UnrefRefundReason.ZERO_DOLLAR_REFUNDABLE: {
      return `The original amount paid by ${retailerPaymentTypeName} on this
      sale has been refunded, a card is required to be presented to
      process the remaining refund. You can continue or choose another
      refund type.`
    }
    case UnrefRefundReason.MULTIPLE_PAYMENTS: {
      return 'Due to the multiple cards that the customer used in the original sale, a card is required to be presented to process the refund. You can continue or choose another refund'
    }
    case UnrefRefundReason.BLIND_REFUND: {
      return 'Because this refund isn’t linked to the original sale, a card is required for this refund to be processed. You can continue or choose another refund type'
    }
    case UnrefRefundReason.DIFFERENT_PAYMENT_TYPES: {
      return 'The original sale was paid using a different payment type so a card must be presented to process the refund. You can continue or choose another refund type.'
    }
    default: {
      // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
      const _exhaustiveCheck: never = reason
      return _exhaustiveCheck
    }
  }
}

export const LSPAYRefundClientDriven: FC<LSPAYRefundClientDrivenProps> = ({
  register,
  retailerPaymentType,
  originalRegisterSalePaymentIds,
  originalSalePaidByOtherPT,
  originalSaleHasProcessingServicePayment,
  originalLspayPaymentAmount,
  outletId,
  amount,
  transactionId,
  currency,
  onSuccess,
  onError,
  setDismissible,
}) => {
  const [refundState, dispatch] = useRefundEffectReducer({
    amount,
    currency,
    originalRegisterSalePaymentIds,
    originalSalePaidByOtherPT,
    originalSaleHasProcessingServicePayment,
    originalLspayPaymentAmount,
    outletId,
    register,
    retailerPaymentType,
    transactionId,
  })

  useEffect(() => {
    if (refundState.status === 'done') {
      onSuccess(Promise.resolve(refundState.transaction))
    }
  }, [onSuccess, refundState])

  useEffect(() => {
    setDismissible(isDismissible(refundState))
  }, [refundState, setDismissible])

  const getRefundContent = (): ReactNode => {
    switch (refundState.status) {
      case 'amountExceededPrompt': {
        const { retailerPaymentType, refundableAmount } = refundState
        return (
          <>
            <ModalHeader>
              The refund amount is more than the original amount paid by{' '}
              {retailerPaymentType.name} on this sale.
            </ModalHeader>
            <ModalContent>
              <p className="vd-p">
                The original amount paid by {retailerPaymentType.name} on this
                sale is <strong>${refundableAmount}</strong>. You can refund
                this original amount to the card that made the original payment,
                then refund the balance with another payment type. Alternatively
                you can refund the full amount unlinked to the original payment
                to any card present.
              </p>
            </ModalContent>
            <ModalActions>
              <div className="vd-btn-group">
                <Button
                  variant="supplementary"
                  onClick={() => dispatch('CONTINUE_WITH_UNREFERENCED')}
                  data-track="refund-full-amount"
                >
                  Refund Full Amount
                </Button>
                <Button
                  variant="go"
                  onClick={() =>
                    dispatch({
                      type: 'REFUND_ORIGINAL_AMOUNT',
                      amount: vn(refundableAmount),
                    })
                  }
                  data-track="refund-original-amount"
                >
                  Refund Original Amount
                </Button>
              </div>
            </ModalActions>
          </>
        )
      }
      case 'unreferencedPrompt': {
        return (
          <>
            <ModalHeader>{CARD_PRESENT_NEEDED_HEADER}</ModalHeader>
            <ModalContent>
              <p
                className="vd-p"
                data-testid="client-driven-unref-refund-message"
              >
                {getUnreferencedMessage(
                  refundState.unrefReason,
                  retailerPaymentType.name
                )}
              </p>
            </ModalContent>
            <ModalActions>
              <div className="vd-btn-group">
                <Button
                  data-track="choose-another-refund-type-unreferenced"
                  variant="supplementary"
                  onClick={() => onError()}
                >
                  {CHOOSE_ANOTHER_REFUND_TYPE}
                </Button>
                <Button
                  variant="go"
                  onClick={() => dispatch('CONTINUE_WITH_UNREFERENCED')}
                  data-track="continue-with-unreferenced"
                >
                  Continue
                </Button>
              </div>
            </ModalActions>
          </>
        )
      }
      case 'interacPresentPrompt': {
        return (
          <>
            <ModalHeader>{CARD_PRESENT_NEEDED_HEADER}</ModalHeader>
            <ModalContent>
              <p className="vd-p">
                Due to the type of card that the customer used in the original
                sale, the same card is required to be presented to process the
                refund. You can continue or choose another refund type.
              </p>
            </ModalContent>
            <ModalActions>
              <div className="vd-btn-group">
                <Button
                  data-track="choose-another-refund-type-interace-present"
                  variant="supplementary"
                  onClick={() => onError()}
                >
                  {CHOOSE_ANOTHER_REFUND_TYPE}
                </Button>
                <Button
                  variant="go"
                  onClick={() => dispatch('CONTINUE_WITH_CARD_PRESENT')}
                  data-track="continue-with-card-present"
                >
                  Continue
                </Button>
              </div>
            </ModalActions>
          </>
        )
      }
      case 'fetchingPairing': {
        return <CheckingPairing />
      }
      case 'connectingToTerminal':
      case 'creatingSetupIntent':
        return <ConnectingToTerminal />
      case 'readingReusableCard':
      case 'refundingViaCardPresent': {
        let modalActions = null
        if (refundState.status === 'refundingViaCardPresent') {
          modalActions = (
            <Button
              variant="no"
              className="vd-mr2"
              onClick={() => {
                refundState.terminal
                  .cancelCollectRefundPaymentMethod()
                  .catch(() => {
                    // absorb this error, there's nothing to do about it
                  })
              }}
              data-track="cancel-collect-refund-payment"
            >
              Cancel Refund
            </Button>
          )
        }
        if (refundState.status === 'readingReusableCard') {
          modalActions = (
            <Button
              variant="no"
              className="vd-mr2"
              onClick={() => {
                refundState.terminal.cancelReadReusableCard().catch(() => {
                  // absorb this error, there's nothing to do about it
                })
              }}
              data-track="cancel-read-reusable-card"
            >
              Cancel Refund
            </Button>
          )
        }

        return (
          <LSPAYModalContent actions={modalActions}>
            <VendImage
              relativeImagePath="payments/insert-card-v1.svg"
              relativeDarkImagePath="payments/insert-card-darkmode-v1.svg"
              className="vd-mb2"
            />
            <h2 className="vd-align-center">
              {refundState.refundMethod.type === 'unreferenced'
                ? `Insert or swipe the customer's card.`
                : `Tap, insert, or swipe the customer's card\nused in the original sale.`}
            </h2>
          </LSPAYModalContent>
        )
      }
      case 'collectingSetupIntentPaymentMethod': {
        return (
          <LSPAYModalContent
            actions={
              <Button
                variant="no"
                className="vd-mr2"
                onClick={() => {
                  refundState.terminal
                    .cancelCollectSetupIntentPaymentMethod()
                    .catch(() => {
                      // absorb this error, there's nothing to do about it
                    })
                }}
                data-track="cancel-collect-setup-intent-payment"
              >
                Cancel Refund
              </Button>
            }
          >
            <VendImage
              relativeImagePath="payments/insert-card-v1.svg"
              relativeDarkImagePath="payments/insert-card-darkmode-v1.svg"
              className="vd-mb2"
            />
            <h2 className="vd-align-center">
              Tap, insert or swipe the customer's card
            </h2>
            <div className="vd-align-center">
              <p>
                The customer card details will be securely stored only to
                process this refund.
                <br />
                Ensure the customer agrees for the card details to be stored.
              </p>
            </div>
          </LSPAYModalContent>
        )
      }
      case 'persistingCardPresentRefund':
      case 'performingUnreferencedRefund':
      case 'refundingViaCardNotPresent': {
        return <Processing isRefund={true} />
      }
      case 'done': {
        // Modal should be closed if the state was done.
        // Hence we can just return
        return
      }
      case 'error':
        return (
          <ErrorContent
            lspayError={{
              ...refundState.errorMessage,
              action: CHOOSE_ANOTHER_REFUND_TYPE,
            }}
            disableRetry={!refundState.retryable}
            retry={() => dispatch('RETRY')}
            close={() => {
              onError()
            }}
          />
        )
      default: {
        // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
        const _exhaustiveCheck: never = refundState
        return _exhaustiveCheck
      }
    }
  }

  return <>{getRefundContent()}</>
}
